import { useState } from "react";
import { toast } from "react-toastify";
import {
  ArrowUpTrayIcon,
  DocumentIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  callingCountryAllowed,
  currencyFormatter,
  IAvailablePhoneNumber,
  IOrganisationPhoneNumberItem,
  IPhoneNumberRegulationResponse,
  IPhoneNumbersResponse,
  IRegulatoryAddress,
  IRegulatoryDocumentInput,
  IRegulationField,
  PHONE_NUMBER_COUNTRIES,
  PermissionLevel,
  PHONE_NUMBER_MONTHLY_PRICE_CENTS,
  PhoneNumberCountry,
  RegulatoryBundleStatus,
  RegulatoryEndUserType,
} from "shared";

import { useGetSelectedOrganisation } from "../../hooks/useGetSelectedOrganisation";
import { useSettings } from "../../hooks/useSettings";
import { useUser } from "../../hooks/useUser";
import {
  useBuyPhoneNumberMutation,
  useFetchPhoneNumbersQuery,
  useLazyFetchAvailablePhoneNumbersQuery,
  useLazyFetchPhoneNumberRegulationQuery,
  useReleasePhoneNumberMutation,
  useSubmitRegulatoryBundleMutation,
  useTrackBuyPhoneNumberStartedMutation,
  useUpdateCallSettingsMutation,
  useUpdatePhoneNumberMutation,
} from "../../services/api/contactsApi/contact";
import { Button } from "../../../common/Atoms/Button";
import { Card } from "../../../common/Atoms/Card";
import { FormLabel } from "../../../common/Atoms/FormLabel";
import { InputTextAddon } from "../../../common/Atoms/InputTextAddon";
import { Modal } from "../../../common/Atoms/Modal";
import { MultiButtonCardHeader } from "../../../common/Atoms/MultiButtonCardHeader";
import { Notification } from "../../../common/Atoms/Notification";
import { RadioCards } from "../../../common/Atoms/RadioCards";
import { Select } from "../../../common/Atoms/Select";
import { SmallSpinner } from "../../../common/Atoms/SmallSpinner";
import { TextArea } from "../../../common/Atoms/TextArea";
import { Toggle } from "../../../common/Atoms/Toggle";

const MAX_FILE_BYTES = 10 * 1024 * 1024;
const MAX_MESSAGE_LENGTH = 500;

const countryName = (code: string) => PHONE_NUMBER_COUNTRIES.find(c => c.code === code)?.name || code;

type NextNumber = IPhoneNumbersResponse[`nextNumber`];

/** What buying a number in the country costs, e.g. "AUD 5.00 per month". Null when the currency has no price */
function numberPrice(next: NextNumber, country: PhoneNumberCountry): string | null {
  if (next.included) return `Included with your plan`;

  const cents = next.currency ? PHONE_NUMBER_MONTHLY_PRICE_CENTS[country][next.currency] : undefined;

  if (cents === undefined) return null;

  return `${currencyFormatter(next.currency)((cents * (next.annual ? 12 : 1)) / 100)} per ${next.annual ? `year` : `month`}, added to your subscription`;
}

const errorMessage = (err: unknown) => (err as { data?: { message?: string } })?.data?.message || `Something went wrong, please try again`;

/** Twilio's regulation text uses markdown links, `[label](url)`. Split gives text, label, url, text, ... */
const withLinks = (text: string) => text.split(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/).map((part, i, parts) => {
  if (i % 3 === 0) return part;
  if (i % 3 === 2) return null;

  return (
    <a
      key={ i }
      href={ parts[i + 1] }
      target={ `_blank` }
      rel={ `noreferrer` }
      className={ `text-paidniceGreen-600 hover:text-paidniceGreen-700 underline` }
    >
      { part }
    </a>
  );
});

/** One document per type: it covers every requirement that accepts the type */
interface IDocState {
  fields: Record<string, string>;
  file: File | null;
}

export function PhoneNumbers() {
  const currentOrg = useGetSelectedOrganisation();
  const { data: user } = useUser();
  const organisationId = currentOrg?.id;
  const canEdit = !!currentOrg?.users?.some(orgUser => orgUser.id === user?.id && orgUser.permissionSettings === PermissionLevel.EDITOR);

  const { data, isLoading } = useFetchPhoneNumbersQuery({ organisationId }, { skip: !organisationId });
  const [updateNumber, { isLoading: saving }] = useUpdatePhoneNumberMutation();
  const [releaseNumber, { isLoading: releasing }] = useReleasePhoneNumberMutation();
  const [trackBuyStarted] = useTrackBuyPhoneNumberStartedMutation();
  const { data: settings } = useSettings();
  const [updateCallSettings, { isLoading: savingCallSettings }] = useUpdateCallSettingsMutation();

  const [buyOpen, setBuyOpen] = useState(false);
  const [editing, setEditing] = useState<IOrganisationPhoneNumberItem | null>(null);
  const [message, setMessage] = useState(``);
  const [releasingNumber, setReleasingNumber] = useState<IOrganisationPhoneNumberItem | null>(null);

  const openEdit = (number: IOrganisationPhoneNumberItem) => {
    setMessage(number.inboundMessage || data?.defaultInboundMessage || ``);
    setEditing(number);
  };

  const save = async(inboundMessage: string) => {
    try {
      await updateNumber({ organisationId, id: editing.id, inboundMessage: inboundMessage.trim() || null }).unwrap();
      toast.success(`Message saved`);
      setEditing(null);
    }
    catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const release = async() => {
    try {
      await releaseNumber({ organisationId, id: releasingNumber.id }).unwrap();
      toast.success(`Number released`);
      setReleasingNumber(null);
    }
    catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const setAutoTranscribe = async({ value }: { value: boolean }) => {
    try {
      await updateCallSettings({ organisationId, autoTranscribeCalls: value }).unwrap();
    }
    catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const bundleLines = (data?.bundles || []).filter(b => [`pending-review`, `in-review`, `twilio-rejected`].includes(b.status));

  return (
    <Card className={ `mb-6` }>
      <MultiButtonCardHeader
        title={ `Phone numbers` }
        variant={ `plain` }
        description={ `Calls currently show a shared Paidnice number as the caller ID. Buy a number in a country and calls to that country will show your own number instead.` }
        buttons={ canEdit ? [{ buttonText: `Buy a number`, onClick: () => {
          setBuyOpen(true);
          // Product signal only, so a failure is ignored
          trackBuyStarted({ organisationId }).unwrap().catch(() => undefined);
        } }] : [] }
      />

      <Notification
        type={ `info` }
        className={ `mt-4` }
      >
        { `Anyone who rings your number hears a spoken message. Better incoming call routing is coming soon.` }
      </Notification>

      <div className={ `mt-4 divide-y divide-gray-100` }>
        { isLoading ? <SmallSpinner colored /> : null }
        { !isLoading && !data?.numbers.length ? (
          <p className={ `text-sm text-gray-500` }>{ `You have no phone numbers yet.` }</p>
        ) : null }
        { data?.numbers.map(number => (
          <div
            key={ number.id }
            className={ `flex items-center justify-between gap-4 py-3` }
          >
            <div className={ `min-w-0` }>
              <div className={ `text-sm font-medium text-gray-900` }>{ `${number.phoneNumber} (${countryName(number.isoCountry)})` }</div>
              <div className={ `truncate text-sm text-gray-500` }>{ number.inboundMessage || `Default message` }</div>
            </div>
            { canEdit ? (
              <div className={ `flex shrink-0 gap-2` }>
                <Button
                  size={ `sm` }
                  color={ `white` }
                  onClick={ () => openEdit(number) }
                >
                  { `Edit message` }
                </Button>
                <Button
                  size={ `sm` }
                  color={ `paidniceRedOutline` }
                  onClick={ () => setReleasingNumber(number) }
                >
                  { `Release` }
                </Button>
              </div>
            ) : null }
          </div>
        )) }
      </div>

      { bundleLines.map(bundle => (
        <p
          key={ bundle.id }
          className={ `mt-3 text-sm ${bundle.status === `twilio-rejected` ? `text-red-700` : `text-gray-600`}` }
        >
          { bundle.status === `twilio-rejected`
            ? `Twilio rejected your ${countryName(bundle.isoCountry)} details: ${bundle.failureReason || `no reason given`}`
            : `Twilio is reviewing your ${countryName(bundle.isoCountry)} details, usually up to 3 business days` }
        </p>
      )) }

      <div className={ `mt-4 border-t border-gray-100 pt-4` }>
        <Toggle
          label={ `Auto transcribe calls` }
          description={ `Record and transcribe every call from the moment it is answered, without clicking Start Transcribing.` }
          helpText={ `You are responsible for checking the call recording and consent laws that apply where you and the people you call are, and for getting consent where they require it.` }
          checked={ !!settings?.autoTranscribeCalls }
          onChange={ setAutoTranscribe }
          disabled={ !canEdit || !settings }
          loading={ savingCallSettings }
        />
      </div>

      { buyOpen && data ? (
        <BuyModal
          organisationId={ organisationId }
          nextNumber={ data.nextNumber }
          orgName={ currentOrg?.displayName || currentOrg?.legalName || `` }
          onClose={ () => setBuyOpen(false) }
        />
      ) : null }

      <Modal
        open={ !!editing }
        setOpen={ open => !open && setEditing(null) }
        title={ `Edit message` }
      >
        <div className={ `mt-4` }>
          <TextArea
            label={ `Message spoken to people who ring ${editing?.phoneNumber || `this number`}` }
            value={ message }
            rows={ 4 }
            onChange={ ({ value }) => setMessage(String(value).slice(0, MAX_MESSAGE_LENGTH)) }
            description={ `${message.length}/${MAX_MESSAGE_LENGTH} characters` }
          />
          <div className={ `mt-4 flex justify-end gap-2` }>
            <Button
              color={ `white` }
              disabled={ saving }
              onClick={ () => save(``) }
            >
              { `Use default` }
            </Button>
            <Button
              loading={ saving }
              onClick={ () => save(message) }
            >
              { `Save` }
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={ !!releasingNumber }
        setOpen={ open => !open && setReleasingNumber(null) }
        title={ `Release number` }
      >
        <p className={ `mt-4 text-sm text-gray-600` }>
          { `Release ${releasingNumber?.phoneNumber}? Calls to ${countryName(releasingNumber?.isoCountry || ``)} will go back to showing the shared Paidnice number, and this number cannot be recovered.` }
        </p>
        <div className={ `mt-4 flex justify-end gap-2` }>
          <Button
            color={ `white` }
            disabled={ releasing }
            onClick={ () => setReleasingNumber(null) }
          >
            { `Cancel` }
          </Button>
          <Button
            color={ `paidniceRed` }
            loading={ releasing }
            onClick={ release }
          >
            { `Release` }
          </Button>
        </div>
      </Modal>
    </Card>
  );
}

function BuyModal({ organisationId, nextNumber, orgName, onClose }: { organisationId: string; nextNumber: NextNumber; orgName: string; onClose: () => void }) {
  const [country, setCountry] = useState<PhoneNumberCountry | null>(null);
  const featureFlags = useGetSelectedOrganisation()?.featureFlags || [];
  const [fetchRegulation, { currentData: reg, isFetching: regLoading }] = useLazyFetchPhoneNumberRegulationQuery();
  const [submitted, setSubmitted] = useState(false);

  const pickCountry = (code: PhoneNumberCountry) => {
    setCountry(code);
    setSubmitted(false);
    fetchRegulation({ organisationId, isoCountry: code });
  };

  const status: RegulatoryBundleStatus | undefined = reg?.bundle?.status;
  const needsCompliance = !!reg?.required && status !== `twilio-approved`;
  const reviewing = submitted || (needsCompliance && (status === `pending-review` || status === `in-review` || status === `provisionally-approved`));

  return (
    <Modal
      open
      setOpen={ open => !open && onClose() }
      title={ `Buy a number` }
      width={ `lg` }
    >
      <div className={ `mt-4 space-y-4` }>
        <Select
          label={ `Country` }
          options={ PHONE_NUMBER_COUNTRIES.filter(c => callingCountryAllowed(c.code, featureFlags)).map(c => ({ label: c.name, value: c.code })) }
          selected={ country }
          onChange={ ({ value }) => pickCountry(value) }
        />
        { country ? <p className={ `text-sm text-gray-600` }>{ numberPrice(nextNumber, country) }</p> : null }
        { regLoading ? <SmallSpinner colored /> : null }
        { country && reg && !regLoading ? (
          reviewing ? (
            <div>
              <Notification type={ `info` }>
                { `Twilio is reviewing your ${countryName(country)} details, usually up to 3 business days. There is nothing else to do for now. You can buy a number once they approve.` }
              </Notification>
              <div className={ `mt-4 flex justify-end` }>
                <Button onClick={ onClose }>{ `Close` }</Button>
              </div>
            </div>
          ) : needsCompliance ? (
            <ComplianceForm
              key={ reg.endUserType }
              organisationId={ organisationId }
              orgName={ orgName }
              country={ country }
              reg={ reg }
              onEndUserTypeChange={ endUserType => fetchRegulation({ organisationId, isoCountry: country, endUserType }) }
              onSubmitted={ () => setSubmitted(true) }
            />
          ) : !nextNumber.included && !nextNumber.canBuyPaid ? (
            <Notification type={ `info` }>
              { `Your plan includes one free number. Buying another needs an active subscription.` }
            </Notification>
          ) : (
            <NumberSearch
              organisationId={ organisationId }
              country={ country }
              price={ numberPrice(nextNumber, country) }
              onBought={ onClose }
            />
          )
        ) : null }
      </div>
    </Modal>
  );
}

function ComplianceForm({ organisationId, orgName, country, reg, onEndUserTypeChange, onSubmitted }: {
  organisationId: string;
  orgName: string;
  country: PhoneNumberCountry;
  reg: IPhoneNumberRegulationResponse;
  onEndUserTypeChange: (endUserType: RegulatoryEndUserType) => void;
  onSubmitted: () => void;
}) {
  const isBusiness = reg.endUserType === `business`;
  const [submit, { isLoading }] = useSubmitRegulatoryBundleMutation();
  const [address, setAddress] = useState<IRegulatoryAddress>({
    customerName: isBusiness ? orgName : ``,
    street: ``,
    streetSecondary: ``,
    city: ``,
    region: ``,
    postalCode: ``,
  });
  const [endUser, setEndUser] = useState<Record<string, string>>({});
  // requirementName -> document type the user picked, and type -> that document's details
  const [picked, setPicked] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [docs, setDocs] = useState<Record<string, IDocState>>({});

  const endUserFields = reg.regulation?.endUser[0]?.fields || [];
  const requirements = reg.regulation?.supportingDocuments || [];

  // Until the user picks, prefer a type an earlier requirement already uses, so one extract covers all it can
  const types = requirements.reduce<string[]>((acc, r) => [
    ...acc,
    picked[r.requirementName] || r.acceptedDocuments.find(o => acc.includes(o.type))?.type || r.acceptedDocuments[0].type,
  ], []);
  const docState = (type: string): IDocState => docs[type] || { fields: {}, file: null };
  const setDoc = (type: string, patch: Partial<IDocState>) => setDocs(d => ({ ...d, [type]: { ...docState(type), ...patch } }));

  const addressOk = !!(address.customerName.trim() && address.street.trim() && address.city.trim() && address.region.trim() && address.postalCode.trim());
  const endUserOk = endUserFields.every(f => endUser[f.machineName]?.trim());
  const docsOk = requirements.every((r, i) => {
    const option = r.acceptedDocuments.find(o => o.type === types[i]);

    return option.fields.every(f => docState(types[i]).fields[f.machineName]?.trim());
  });

  const send = async() => {
    const formData = new FormData();
    const files: File[] = [];
    const documents: IRegulatoryDocumentInput[] = [...new Set(types)].map(type => {
      const state = docState(type);
      const doc: IRegulatoryDocumentInput = { type, fields: state.fields };
      if (state.file) {
        doc.fileIndex = files.length;
        files.push(state.file);
      }

      return doc;
    });

    formData.append(`isoCountry`, country);
    formData.append(`endUserType`, reg.endUserType);
    formData.append(`address`, JSON.stringify(address));
    formData.append(`endUser`, JSON.stringify(endUser));
    formData.append(`documents`, JSON.stringify(documents));
    files.forEach(file => formData.append(`files`, file));

    setSubmitError(null);

    try {
      await submit({ organisationId, formData }).unwrap();
      onSubmitted();
    }
    catch (err) {
      setSubmitError(errorMessage(err));
    }
  };

  const fieldInput = (field: IRegulationField, value: string, onChange: (value: string) => void) => (
    <InputTextAddon
      key={ field.machineName }
      label={ field.friendlyName }
      value={ value }
      description={ <>{ withLinks([field.description, field.constraint].filter(Boolean).join(` `)) }</> }
      onChange={ ({ value }) => onChange(String(value)) }
    />
  );

  const addressInput = (key: keyof IRegulatoryAddress, label: string) => (
    <InputTextAddon
      label={ label }
      value={ address[key] || `` }
      onChange={ ({ value }) => setAddress(a => ({ ...a, [key]: String(value) })) }
    />
  );

  return (
    <div className={ `space-y-4` }>
      { reg.bundle?.status === `twilio-rejected` ? (
        <Notification type={ `error` }>
          { `Twilio rejected your last submission: ${reg.bundle.failureReason || `no reason given`}. Please check your details and send them again.` }
        </Notification>
      ) : null }
      <p className={ `text-sm text-gray-600` }>
        { `${countryName(country)} requires some details about who will use the number before you can buy one. Twilio reviews them, usually within 3 business days.` }
      </p>

      <RadioCards
        label={ `Who is the number for?` }
        options={ [
          { value: `business`, title: `Business`, description: `A company or a registered business name` },
          { value: `individual`, title: `Individual`, description: `A sole trader trading under their own name` },
        ] }
        selected={ reg.endUserType }
        cols={ 2 }
        onChange={ ({ value }) => onEndUserTypeChange(value as RegulatoryEndUserType) }
      />

      <h3 className={ `text-sm font-semibold text-gray-900` }>{ isBusiness ? `Business address` : `Address` }</h3>
      { addressInput(`customerName`, isBusiness ? `Business name` : `Full name`) }
      { addressInput(`street`, `Street`) }
      { addressInput(`streetSecondary`, `Street line 2 (optional)`) }
      <div className={ `grid grid-cols-1 gap-4 sm:grid-cols-3` }>
        { addressInput(`city`, `City`) }
        { addressInput(`region`, `State / region`) }
        { addressInput(`postalCode`, `Postal code`) }
      </div>
      <p className={ `text-sm text-gray-500` }>{ `Country: ${countryName(country)}` }</p>

      { endUserFields.length ? <h3 className={ `text-sm font-semibold text-gray-900` }>{ isBusiness ? `Business details` : `Your details` }</h3> : null }
      { endUserFields.map(f => fieldInput(f, endUser[f.machineName] || ``, value => setEndUser(e => ({ ...e, [f.machineName]: value })))) }

      { requirements.map((r, i) => {
        const type = types[i];
        const state = docState(type);
        const option = r.acceptedDocuments.find(o => o.type === type);
        const owner = types.indexOf(type);
        // Fields an earlier block for the same document already asks for
        const asked = new Set(requirements.slice(0, i).flatMap((earlier, j) => types[j] === type ?
          earlier.acceptedDocuments.find(o => o.type === type).fields.map(f => f.machineName) :
          []));

        return (
          <div
            key={ r.requirementName }
            className={ `space-y-4 rounded-lg border border-gray-200 p-4` }
          >
            <h3 className={ `text-sm font-semibold text-gray-900` }>{ r.name }</h3>
            { r.description ? <p className={ `text-sm text-gray-500` }>{ withLinks(r.description) }</p> : null }
            { r.acceptedDocuments.length > 1 ? (
              <Select
                label={ `Document type` }
                options={ r.acceptedDocuments.map(o => ({ label: o.name, value: o.type })) }
                selected={ type }
                onChange={ ({ value }) => setPicked(p => ({ ...p, [r.requirementName]: value })) }
              />
            ) : null }
            { option.fields
              .filter(f => !asked.has(f.machineName))
              .map(f => fieldInput(f, state.fields[f.machineName] || ``, value => setDoc(type, { fields: { ...state.fields, [f.machineName]: value } }))) }
            { owner < i ? (
              <p className={ `text-sm text-gray-600` }>{ `Covered by the document for ${requirements[owner].name}.` }</p>
            ) : (
              <DocumentFileInput
                id={ `bundle-file-${r.requirementName}` }
                file={ state.file }
                onChange={ file => setDoc(type, { file }) }
              />
            ) }
          </div>
        );
      }) }

      { submitError ? <Notification type={ `error` }>{ submitError }</Notification> : null }

      <div className={ `flex justify-end` }>
        <Button
          loading={ isLoading }
          disabled={ !addressOk || !endUserOk || !docsOk || isLoading }
          onClick={ send }
        >
          { `Submit for review` }
        </Button>
      </div>
    </div>
  );
}

function NumberSearch({ organisationId, country, price, onBought }: { organisationId: string; country: PhoneNumberCountry; price: string | null; onBought: () => void }) {
  const [search, { currentData, isFetching, isUninitialized }] = useLazyFetchAvailablePhoneNumbersQuery();
  const [buy, { isLoading: buying }] = useBuyPhoneNumberMutation();
  const [areaCode, setAreaCode] = useState(``);
  const [contains, setContains] = useState(``);
  const [confirming, setConfirming] = useState<IAvailablePhoneNumber | null>(null);
  const [buyError, setBuyError] = useState<string | null>(null);

  const supportsAreaCode = !!PHONE_NUMBER_COUNTRIES.find(c => c.code === country)?.areaCodeSearch;
  const areaCodeOk = !areaCode || /^\d{3}$/.test(areaCode);

  const runSearch = () => {
    setConfirming(null);
    setBuyError(null);

    search({
      organisationId,
      isoCountry: country,
      areaCode: supportsAreaCode && areaCode ? areaCode : undefined,
      contains: contains || undefined,
    });
  };

  const confirmBuy = async() => {
    setBuyError(null);

    try {
      await buy({ organisationId, isoCountry: country, phoneNumber: confirming.phoneNumber }).unwrap();
      toast.success(`You now own ${confirming.phoneNumber}`);
      onBought();
    }
    catch (err) {
      setBuyError(errorMessage(err));
    }
  };

  return (
    <div className={ `space-y-4` }>
      <div className={ `grid grid-cols-1 gap-4 sm:grid-cols-2` }>
        { supportsAreaCode ? (
          <InputTextAddon
            label={ `Area code (optional)` }
            value={ areaCode }
            maxLength={ 3 }
            description={ areaCodeOk ? undefined : `Use 3 digits` }
            onChange={ ({ value }) => setAreaCode(String(value).replace(/\D/g, ``)) }
          />
        ) : null }
        <InputTextAddon
          label={ `Contains digits (optional)` }
          value={ contains }
          onChange={ ({ value }) => setContains(String(value).replace(/\D/g, ``)) }
        />
      </div>
      <Button
        loading={ isFetching }
        disabled={ !areaCodeOk || isFetching }
        onClick={ runSearch }
      >
        { `Search` }
      </Button>

      { !isUninitialized && !isFetching && !currentData?.numbers.length ? (
        <p className={ `text-sm text-gray-500` }>{ `No numbers found. Try different digits.` }</p>
      ) : null }
      <div className={ `divide-y divide-gray-100` }>
        { currentData?.numbers.map(n => (
          <div
            key={ n.phoneNumber }
            className={ `flex items-center justify-between gap-4 py-3` }
          >
            <div>
              <div className={ `text-sm font-medium text-gray-900` }>{ n.friendlyName }</div>
              <div className={ `text-xs text-gray-500` }>{ [n.locality, n.region].filter(Boolean).join(`, `) }</div>
            </div>
            <Button
              size={ `sm` }
              color={ `white` }
              onClick={ () => setConfirming(n) }
            >
              { `Buy` }
            </Button>
          </div>
        )) }
      </div>

      { confirming ? (
        <Notification type={ `warning` }>
          <p>{ `Buy ${confirming.friendlyName}? It becomes your caller ID for calls to ${countryName(country)}.${price ? ` ${price}.` : ``}` }</p>
          <div className={ `mt-3 flex gap-2` }>
            <Button
              size={ `sm` }
              loading={ buying }
              onClick={ confirmBuy }
            >
              { `Confirm` }
            </Button>
            <Button
              size={ `sm` }
              color={ `white` }
              disabled={ buying }
              onClick={ () => {
                setConfirming(null);
                setBuyError(null);
              } }
            >
              { `Cancel` }
            </Button>
          </div>
        </Notification>
      ) : null }
      { buyError ? <Notification type={ `error` }>{ buyError }</Notification> : null }
    </div>
  );
}

/** File picker for a compliance document: a clear upload button, then the chosen file with a way to remove it */
function DocumentFileInput({ id, file, onChange }: { id: string; file: File | null; onChange: (file: File | null) => void }) {
  if (file) {
    return (
      <div>
        <FormLabel>{ `Document file` }</FormLabel>
        <div className={ `mt-2 flex items-center gap-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2` }>
          <DocumentIcon className={ `h-5 w-5 shrink-0 text-gray-400` } />
          <div className={ `min-w-0 flex-1` }>
            <div className={ `truncate text-sm font-medium text-gray-900` }>{ file.name }</div>
            <div className={ `text-xs text-gray-500` }>{ `${(file.size / 1024 / 1024).toFixed(1)} MB` }</div>
          </div>
          <button
            type={ `button` }
            onClick={ () => onChange(null) }
            className={ `cursor-pointer rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-600` }
            aria-label={ `Remove ${file.name}` }
          >
            <XMarkIcon className={ `h-4 w-4` } />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <FormLabel>{ `Document file (optional)` }</FormLabel>
      <label
        htmlFor={ id }
        className={ `mt-2 flex cursor-pointer flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed border-gray-300 px-4 py-5 text-center hover:border-paidniceGreen-500 hover:bg-paidniceGreen-50 focus-within:border-paidniceGreen-500` }
      >
        <ArrowUpTrayIcon className={ `h-6 w-6 text-gray-400` } />
        <span className={ `text-sm font-medium text-paidniceGreen-700` }>{ `Upload document` }</span>
        <span className={ `text-xs text-gray-500` }>{ `PDF, JPG or PNG, up to 10MB` }</span>
        <input
          id={ id }
          type={ `file` }
          accept={ `.pdf,.jpg,.jpeg,.png` }
          className={ `sr-only` }
          onChange={ e => {
            const picked = e.target.files?.[0] || null;

            e.target.value = ``;

            if (picked && picked.size > MAX_FILE_BYTES) {
              toast.error(`That file is over 10MB`);

              return;
            }

            onChange(picked);
          } }
        />
      </label>
    </div>
  );
}
